import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Box, SelectChangeEvent, Button } from '@mui/material';
import { ChartFilters } from './components/ChartFilters';
import { ChartContent } from './components/ChartContent';
import { DownloadProgress } from './components/DownloadProgress';
import { useChartData, Entity, DataSource, ShowAs, GroupBy } from './hooks/useChartData';

const queryClient = new QueryClient();

interface ChartPageContentProps {
  entities: Entity[];
}

const ChartPageContent = ({ entities = [] }: ChartPageContentProps) => {
  const {
    state: { dataSource, showAs, groupBy, selectedEntities },
    dispatch,
    chartData,
    isPieChart,
    getShowAsOptions,
    getGroupByOptions,
    // Download-related properties and functions
    downloadProgress,
    downloadPastYearData,
    cancelDownload,
    dismissError,
    hasDownloadedData
  } = useChartData(entities);

  const handleDataSourceChange = (event: SelectChangeEvent<DataSource>) => {
    dispatch({ type: 'SET_DATA_SOURCE', payload: event.target.value as DataSource });
  };

  const handleShowAsChange = (event: SelectChangeEvent<ShowAs>) => {
    dispatch({ type: 'SET_SHOW_AS', payload: event.target.value as ShowAs });
  };

  const handleGroupByChange = (event: SelectChangeEvent<GroupBy>) => {
    dispatch({ type: 'SET_GROUP_BY', payload: event.target.value as GroupBy });
  };

  const handleEntitiesChange = (_: React.SyntheticEvent, newValue: Entity[]) => {
    dispatch({ type: 'SET_ENTITIES', payload: newValue });
  };

  // Show download button for applications and jobs data sources as soon as data source is selected
  const showDownloadButton =
    ['applications', 'jobs'].includes(dataSource) && selectedEntities.length > 0;

  return (
    <Box sx={{ p: 3, position: 'relative' }}>
      {/* Download progress and button */}
      {showDownloadButton && (
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            right: '20px',
            position: 'absolute'
          }}
        >
          {downloadProgress.status !== 'idle' && (
            <DownloadProgress
              progress={downloadProgress}
              onCancel={cancelDownload}
              onRetry={downloadPastYearData}
              onDismissError={dismissError}
            />
          )}

          {downloadProgress.status === 'idle' && (
            <Button
              variant="contained"
              color="primary"
              onClick={downloadPastYearData}
              sx={{ ml: 'auto' }}
            >
              Refresh Chart Data
            </Button>
          )}
        </Box>
      )}
      <ChartFilters
        dataSource={dataSource}
        showAs={showAs}
        groupBy={groupBy}
        selectedEntities={selectedEntities}
        entities={entities}
        onDataSourceChange={handleDataSourceChange}
        onShowAsChange={handleShowAsChange}
        onGroupByChange={handleGroupByChange}
        onEntitiesChange={handleEntitiesChange}
        getShowAsOptions={getShowAsOptions}
        getGroupByOptions={getGroupByOptions}
      />

      <ChartContent
        dataSource={dataSource}
        showAs={showAs}
        groupBy={groupBy}
        chartData={chartData}
        chartTitle={chartData?.chartTitle}
        isPieChart={isPieChart}
        onRetry={['applications', 'jobs'].includes(dataSource) ? downloadPastYearData : undefined}
      />
    </Box>
  );
};

interface ChartPageProps {
  entities: Entity[];
}

const ChartPage = ({ entities }: ChartPageProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ChartPageContent entities={entities} />
    </QueryClientProvider>
  );
};

export default ChartPage;
